import { Baggage } from '../BaggageService/types';
import { getNasServiceCategory, getServiceCategory, getTravellersTypes } from './utils';
import {
	AeroexpressFragment,
	CheckinOrderFragment,
	CheckinPassengerFragment,
	CheckoutOrderFragment,
	FlightFragment,
	FlightSeatMapSegmentFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsServicesFragment,
	InsuranceFragment,
	InsuranceProgramFragment,
	NASProductFragment,
	NASStatus,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	PassengerFragment,
	SelectedInsuranceFragment,
	TravellerGdsServiceService,
	Trip_AeroexpressTrip_Fragment
} from '@websky/graphql';
import { Seat } from '../SeatMap/types';
import { DataLayerItemsListName, ItemCategory } from './types';
import { getRelatedSegments } from '../Baggage/UpgradeBaggageDialog/utils';
import { findMinPrice } from '../CheckoutAdditionalService/components/EveryPortLounge/utils';
import { DataLayerService } from './OrderDataLayer/types';

class WebskyServicesAnalytics {
	public static insurances(
		services: InsuranceProgramFragment[],
		travellers: PassengerFragment[] | CheckinPassengerFragment[]
	) {
		return services?.map<DataLayerService>(service => ({
			category: ItemCategory.Insurance,
			name: service.name,
			price: service.totalPrice.amount,
			count: 1,
			forAllSegments: true,
			travellersTypes: getTravellersTypes(travellers)
		}));
	}

	public static selectedInsurances(
		services: SelectedInsuranceFragment[],
		travellers?: PassengerFragment[] | CheckinPassengerFragment[]
	) {
		const insurancesMap = new Map<string, SelectedInsuranceFragment>();

		services?.forEach(service => {
			insurancesMap.set(service.insuranceProgram.code, service);
		});

		const dataLayer: DataLayerService[] = [];

		const travellersTypes = travellers ? getTravellersTypes(travellers) : [];

		insurancesMap.forEach((service, code) => {
			dataLayer.push({
				category: ItemCategory.Insurance,
				name: service.insuranceProgram?.name,
				price: service.insuranceProgram?.totalPrice.amount,
				count: 1,
				forAllSegments: true,
				travellersTypes
			});
		});

		return dataLayer;
	}

	public static aeroexpress(
		services: Trip_AeroexpressTrip_Fragment[],
		travellers: PassengerFragment[] | CheckinPassengerFragment[]
	) {
		try {
			return services.reduce<DataLayerService[]>((newServices, service) => {
				const segmentIds = service.segments?.map(segment => segment.id);

				return [
					...newServices,
					...segmentIds.map(segmentId => ({
						category: ItemCategory.Aeroexpress,
						name: `${service.toStation.direction}-${service.class}`,
						price: service.price.amount,
						count: 1,
						segmentId,
						travellersTypes: getTravellersTypes(travellers)
					}))
				];
			}, []);
		} catch (e) {
			return [];
		}
	}

	public static baggage(services: Baggage[], travellers: PassengerFragment[] | CheckinPassengerFragment[]) {
		return services.reduce<DataLayerService[]>((newServices, serivce) => {
			const segmentIds = [...(getRelatedSegments(serivce.segmentIds) || [])];

			return [
				...newServices,
				...segmentIds.map(segmentId => ({
					category: getServiceCategory(OrderAdditionalServiceGdsServiceServiceType.Baggage, serivce.type),
					name: `${serivce.name}${
						serivce.value?.amount ? `-${serivce.value.amount}${serivce.value.measurement}` : ''
					}`,
					price: serivce.price.amount,
					segmentId,
					count: 1,
					isRecommend: serivce.isRecommended,
					travellersTypes: getTravellersTypes(travellers)
				}))
			];
		}, []);
	}

	public static seat(
		service: Seat,
		segmentId?: string,
		isRecommend?: boolean,
		travellerType?: string
	): DataLayerService {
		return {
			name: service.number,
			price: service.price.amount,
			category: getServiceCategory(OrderAdditionalServiceGdsServiceServiceType.Seat),
			segmentId,
			isRecommend,
			count: 1,
			travellersTypes: travellerType ? [travellerType] : []
		};
	}

	public static seats(
		seatMap: FlightSeatMapSegmentFragment,
		travellers: PassengerFragment[] | CheckinPassengerFragment[]
	): DataLayerService[] {
		const items: DataLayerService[] = [];

		try {
			if (seatMap.availableSeatsCount > 0) {
				seatMap.decks.forEach(deck =>
					deck.rows.forEach(row => {
						row.seats.forEach(seat => {
							if (seat.isAvailable) {
								items.push({
									category: ItemCategory.Seat,
									name: seat.number,
									price: seat.price?.amount,
									segmentId: seatMap.segmentsInfo.id,
									isRecommend: false,
									count: 1,
									travellersTypes: getTravellersTypes(travellers)
								});
							}
						});
					})
				);
			}
		} catch (e) {}

		return items;
	}

	public static convertVipToLayerService(
		service: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
		travellerId: string,
		travellers: PassengerFragment[] | CheckinPassengerFragment[]
	): DataLayerService {
		return {
			category: getServiceCategory(service.type),
			name: (+travellerId + 1).toString(),
			price: service.price.amount,
			segmentId: service.allowedSegments?.[0]?.[0],
			count: 1,
			travellersTypes: getTravellersTypes(travellers)
		};
	}

	public static selectVip(
		service: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
		passengerId: string,
		segmentId: string
	): DataLayerService {
		return {
			name: (+passengerId + 1).toString(),
			price: service.price.amount,
			segmentId,
			count: 1,
			category: getServiceCategory(service.type)
		};
	}

	public static flight(services: FlightFragment['segmentGroups'], travellers: PassengerFragment[]) {
		return services.map<DataLayerService>(flight => ({
			category: ItemCategory.Flight,
			name: flight.fareFamily?.title ?? 'NA',
			price: flight.price.amount,
			segmentIds: flight.segments.map(segment => segment.id),
			travellersTypes: getTravellersTypes(travellers),
			count: 1
		}));
	}

	public static convertGdsServiceToDataLayer(
		service: GdsServicesFragment['services'][0],
		selectedService: TravellerGdsServiceService,
		travellerType: string
	): DataLayerService {
		return {
			category: getServiceCategory(
				service.type,
				service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
					? (service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment).baggageType
					: null
			),
			name: service.name,
			price: service.price.amount,
			segmentId: selectedService.segmentIds?.[0],
			count: selectedService.count,
			travellersTypes: [travellerType]
		};
	}

	public static convertNasServiceToDataLayer(
		service: NASProductFragment,
		travellers: (PassengerFragment | CheckinPassengerFragment)[],
		travellerType?: string
	): DataLayerService {
		return {
			category: getNasServiceCategory(service.category, service.type),
			name: service.name,
			price: findMinPrice(service, travellers).amount,
			segmentId: service.segmentId.toString(),
			count: 1,
			travellersTypes: travellerType ? [travellerType] : getTravellersTypes(travellers)
		};
	}

	public static getPriceFreezeServices(order: CheckoutOrderFragment): DataLayerService[] {
		const priceFreezeService = order.additionalServices?.gdsServices?.services?.find(
			service => service.isPriceFreeze
		);

		if (
			priceFreezeService &&
			order.travellers.some(traveller =>
				traveller.services?.gdsServices?.services?.some(
					selectedService => selectedService.serviceId === priceFreezeService.id
				)
			)
		) {
			return [
				{
					name: 'hold',
					category: ItemCategory.PriceFreeze,
					forAllSegments: true,
					price:
						(priceFreezeService.price?.amount || 0) *
						order.travellers.filter(traveller =>
							traveller?.services?.gdsServices?.services?.some(
								service => service.serviceId === priceFreezeService.id
							)
						)?.length,
					count: 1
				}
			];
		}

		return [];
	}

	public static getVipItemListName(service: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment) {
		if (service?.type === OrderAdditionalServiceGdsServiceServiceType.BusinessLounge) {
			return DataLayerItemsListName.Business;
		}

		return null;
	}

	public static getCheckoutSelectedServices(order: CheckoutOrderFragment, skipConfirmedServices: boolean = true) {
		try {
			if (order.isPriceFreezeProductSelected) {
				const priceFreezeService = order.additionalServices?.gdsServices?.services?.find(
					service => service.isPriceFreeze
				);

				if (
					order.travellers.some(traveller =>
						traveller.services?.gdsServices?.services?.some(
							service => service.serviceId === priceFreezeService.id && !service.confirmedCount
						)
					)
				) {
					return WebskyServicesAnalytics.getPriceFreezeServices(order);
				}
			}

			return [
				...(order.priceToPay.airticket?.amount > 0
					? this.flight(order.flight?.segmentGroups, order.travellers)
					: []),
				...this.getAllSelectedServicesInOrder(
					order.additionalServices?.gdsServices,
					order.additionalServices?.nas,
					order.travellers,
					order.additionalServices?.insurances,
					order.additionalServices?.transfer,
					skipConfirmedServices
				)
			];
		} catch (error) {
			console.log(error);

			return [];
		}
	}

	public static getCheckinSelectedServices(order: CheckinOrderFragment) {
		return [
			...this.getAllSelectedServicesInOrder(
				order.additionalServices?.gdsServices,
				order.additionalServices?.nas,
				order.travellers.map(traveller => ({
					...traveller,
					needOthsSsr: false,
					fares: []
				})),
				order.additionalServices?.insurances,
				order.additionalServices?.transfer
			)
		];
	}

	public static getAllSelectedServicesInOrder(
		gdsServices: GdsServicesFragment,
		nasServices: OrderAdditionalServicesFragment['nas'],
		passengers: (PassengerFragment | CheckinPassengerFragment)[],
		insurances: InsuranceFragment,
		transfer: AeroexpressFragment,
		skipConfirmedServices: boolean = true
	) {
		const itemList: DataLayerService[] = [];

		const servicesMap = new Map(gdsServices?.services?.map(service => [service.id, service]) || []);
		const nasServicesMap = new Map(nasServices?.services?.map(service => [service.id, service]) || []);

		const passengersMap = new Map(passengers.map(passenger => [passenger.id, passenger]));

		passengers.forEach(passenger => {
			if (passenger.services) {
				passenger.services?.gdsServices?.services?.forEach(selectedService => {
					const service = servicesMap.get(selectedService.serviceId);

					if (
						(!skipConfirmedServices || selectedService.count > selectedService.confirmedCount) &&
						!!service
					) {
						itemList.push(
							this.convertGdsServiceToDataLayer(
								service,
								{
									...selectedService,
									count: !skipConfirmedServices
										? selectedService.count
										: selectedService.count - selectedService.confirmedCount
								},
								passenger.type
							)
						);
					}
				});

				passenger.services?.seats
					?.filter(seat => !skipConfirmedServices || !seat.isConfirmed)
					.forEach(selectedSeat => {
						itemList.push(
							this.seat(
								selectedSeat.seat as Seat,
								selectedSeat.segment?.id,
								passenger.preselectedServices?.seats?.some(
									preselected =>
										preselected.seat?.number === selectedSeat.seat?.number &&
										preselected.segment?.id === selectedSeat.segment?.id
								) ?? false,
								passenger.type
							)
						);
					});
			}
		});

		nasServices?.selectedServices?.forEach(selectedService => {
			if (!skipConfirmedServices || selectedService.status !== NASStatus.Confirmed) {
				const nasService = nasServicesMap.get(selectedService.product.id);

				if (nasService) {
					const travellers = selectedService.travellers.map(traveller => passengersMap.get(traveller.id));

					travellers.forEach(traveller => {
						itemList.push(
							WebskyServicesAnalytics.convertNasServiceToDataLayer(nasService, travellers, traveller.type)
						);
					});
				}
			}
		});

		if (insurances?.selectedInsurances) {
			itemList.push(
				...this.selectedInsurances(
					insurances.selectedInsurances.filter(insurance => !skipConfirmedServices || !insurance.policyUrl),
					passengers
				)
			);
		}

		if (transfer?.aeroexpress?.selectedTrips) {
			itemList.push(
				...this.aeroexpress(
					(transfer.aeroexpress.selectedTrips as AeroexpressFragment['aeroexpress']['selectedTrips']).filter(
						trip => !skipConfirmedServices || !trip.tickets?.length
					),
					passengers
				)
			);
		}

		return itemList;
	}
}

export default WebskyServicesAnalytics;
